'use client'

import { useState, useEffect, useCallback } from 'react'
import { KanbanColumn } from './KanbanColumn'
import { useTaskStore } from '../../stores/taskStore'

// ============================================================================
// Types
// ============================================================================

export interface Task {
  id: string
  titulo: string
  status: string
  prioridade: number
  created_at: string
  started_at?: string
  finished_at?: string
  subtask_count?: number
  parent_task_id?: string
  execution_order?: number
}

interface KanbanBoardProps {
  className?: string
  onTaskClick?: (taskId: string) => void
  refreshInterval?: number
}

type ColumnStatus = 'pending' | 'running' | 'needs_review' | 'done'

// ============================================================================
// Component
// ============================================================================

export function KanbanBoard({
  className = '',
  onTaskClick,
  refreshInterval = 5000
}: KanbanBoardProps) {
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [draggedTask, setDraggedTask] = useState<string | null>(null)

  const { getTasks } = useTaskStore()

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch('https://api.lldonha.com/api/tasks')
      const data = await response.json()

      if (data.success) {
        setTasks(data.tasks)
      }
    } catch (error) {
      console.error('Error fetching tasks:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTasks()

    // Auto-refresh
    const interval = setInterval(fetchTasks, refreshInterval)
    return () => clearInterval(interval)
  }, [fetchTasks, refreshInterval])

  // ============================================================================
  // Drag & Drop Handlers
  // ============================================================================

  const handleDragStart = useCallback((taskId: string) => {
    setDraggedTask(taskId)
  }, [])

  const handleDragEnd = useCallback(() => {
    setDraggedTask(null)
  }, [])

  const handleDrop = useCallback(async (newStatus: ColumnStatus) => {
    if (!draggedTask) return

    // Otimistic update
    setTasks(prev =>
      prev.map(task =>
        task.id === draggedTask
          ? { ...task, status: newStatus }
          : task
      )
    )

    // TODO: Update task status via API
    try {
      // await updateTaskStatus(draggedTask, newStatus)
      console.log(`Task ${draggedTask} moved to ${newStatus}`)
    } catch (error) {
      console.error('Error updating task:', error)
      // Revert on error
      fetchTasks()
    }

    setDraggedTask(null)
  }, [draggedTask, fetchTasks])

  // ============================================================================
  // Task Grouping
  // ============================================================================

  const groupedTasks = {
    pending: tasks.filter(t => t.status === 'pending'),
    running: tasks.filter(t => t.status === 'running'),
    needs_review: tasks.filter(t => t.status === 'needs_review'),
    done: tasks.filter(t => ['done', 'approved'].includes(t.status))
  }

  // ============================================================================
  // Render
  // ============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center gap-3 text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span>Carregando tasks...</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`h-full bg-gray-50 dark:bg-gray-900 ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              📋 Kanban Board
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Arraste e solte tasks entre colunas
            </p>
          </div>

          <button
            onClick={fetchTasks}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            🔄 Atualizar
          </button>
        </div>
      </div>

      {/* Kanban Columns */}
      <div className="grid grid-cols-4 gap-4 p-6 h-[calc(100%-80px)] overflow-hidden">
        <KanbanColumn
          title="📥 To Do"
          status="pending"
          tasks={groupedTasks.pending}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          isDragging={draggedTask !== null}
          onTaskClick={onTaskClick}
        />

        <KanbanColumn
          title="🔄 Running"
          status="running"
          tasks={groupedTasks.running}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          isDragging={draggedTask !== null}
          onTaskClick={onTaskClick}
        />

        <KanbanColumn
          title="📝 Review"
          status="needs_review"
          tasks={groupedTasks.needs_review}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          isDragging={draggedTask !== null}
          onTaskClick={onTaskClick}
        />

        <KanbanColumn
          title="✅ Done"
          status="done"
          tasks={groupedTasks.done}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          isDragging={draggedTask !== null}
          onTaskClick={onTaskClick}
        />
      </div>
    </div>
  )
}
